import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { Scan } from '../../../services/entities/impl/blfApi/schemas/scan';

interface LatestScan {
  scanDate: string | null;
  scanID: string | null;
}

export interface LatestScanDict {
  [blockID: string]: LatestScan;
}

/**
 * Finds the latest scan for each block.
 *
 * This hook takes an array of blocks and an array of scans, and returns a dictionary
 * where the keys are block IDs and the values are the latest scan information for each block.
 *
 * Note: The scans array is assumed to be pre-sorted in the most recent order, so we only need
 * to find the first scan for each block.
 *
 * @param {Block[] | undefined} blocks - The array of blocks to find the latest scans for.
 * @param {Scan[] | undefined} scans - The array of scans, pre-sorted in the most recent order.
 * @returns {LatestScanDict} A dictionary with block IDs as keys and the latest scan information as values.
 */
export const useFindLatestScans = (
  blocks: Block[] | undefined,
  scans: Scan[] | undefined
) => {
  const latestScanDict: LatestScanDict = useMemo(() => {
    const dict: LatestScanDict = {};
    if (blocks && scans) {
      for (const block of blocks) {
        const latestScan = scans.find((scan) => scan.blockID === block.id);

        dict[block.id] = latestScan
          ? {
              scanDate: latestScan.scanDate
                ? format(parseISO(latestScan.scanDate), 'MM/dd/yy')
                : null,
              scanID: latestScan.id,
            }
          : {
              scanDate: null,
              scanID: null,
            };
      }
    }
    return dict;
  }, [blocks, scans]);

  return latestScanDict;
};
